import { keyBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmpPosition, useEmployee } from '~/data/employee';
import { useAllEnums } from '~/data/enums';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { editCustomFieldDataOld } from '~/legacy-components/LegacyCustomFields/helper';
import {
  AnswerPreProcess,
  CustomField,
  CustomFieldType,
  FieldAttributes,
  FieldAttributesHash,
  FieldLang,
} from '~/legacy-components/LegacyCustomFields/types';
import { getCurrentLanguage } from '~/localization';
import { fetchFromOldApi } from '~/services/Http';
import { APIs } from '~/services/Http/internalAPIs';

export interface Decision {
  id: number;
  type: string;
  title: string;
  maker: string;
  date: string;
  decisionInfo: string;
  reminderDate: string;
  isSystemGenerated: boolean;
  attachmentId: number;
  attachmentName: string;
  inventoryNumber: number;
  info: string;
  file: File;
}

export interface Data {
  decisionRegistryData: Decision[];
  editDrAllCustomFields: Record<string, CustomField[]>;
  fieldLang: FieldLang[];
  addDrEmptyCustomFields: CustomField[];
  isFetching: boolean;
}

export const useDecisionRegistryData = () => {
  const [data, setData] = useState<Data>({
    decisionRegistryData: [],
    editDrAllCustomFields: {},
    fieldLang: [],
    addDrEmptyCustomFields: [],
    isFetching: true,
  });
  const [customSelectFields, setCustomSelectFields] = useState([]);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedEmployeeId: empId } = useSelectedEmployeeId();
  const DECISION_REGISTRY_GET = `/json/employee/${empId}/decisionregistry`;
  const {
    employee: { empBasicData, empPosition, empPositionPending },
  } = useEmployee();
  const { allEnums } = useAllEnums();
  const { legalEntities } = useAllLegalEntities();
  const empStatus = empBasicData ? empBasicData.fEmpStatus : '';
  const empPositionData =
    empStatus === 'NEW' || (empStatus === 'PENDING' && empPositionPending)
      ? empPositionPending
      : empPosition;
  const empPositionPendingLegalEntity = empPositionPending?.fEmpLegalEntity;
  const { register } = useForm<AnswerPreProcess>();

  const failedToLoad = useCallback(
    (error: { message: string }) =>
      enqueueSnackbar(
        `${t('laFailedToLoad', {
          error: error.message,
        })}`,
        { variant: 'error' },
      ),
    [enqueueSnackbar, t],
  );

  const fetchDecisionRegistryData = useCallback(async () => {
    try {
      const response = await fetchFromOldApi(DECISION_REGISTRY_GET).then((r) =>
        r.json(),
      );
      const fieldAttr: FieldAttributesHash = await fetchFromOldApi(
        APIs.enum.customFieldAttribute('DECISION_REGISTRY'),
      )
        .then((r) => r.json())
        .then((dataCustomFieldAttr: FieldAttributes[]) =>
          keyBy(dataCustomFieldAttr, 'fCustomDataAttributeName'),
        )
        .catch((err) => {
          failedToLoad(err);
          return {};
        });
      const fieldLang = await fetchFromOldApi(
        APIs.enum.groupCodes(
          'CUSTOM_ENTITY_DECISION_REGISTRY',
          getCurrentLanguage(),
        ),
      )
        .then((r) => r.json())
        .then((dataFieldLang: FieldLang[]) =>
          dataFieldLang.sort((a, b) => a.seq - b.seq),
        )
        .catch((err) => {
          failedToLoad(err);
          return [] as FieldLang[];
        });
      const fieldValues = await fetchFromOldApi(
        APIs.enum.customFields('DECISION_REGISTRY', `${empId}`),
      )
        .then((r) => r.json())
        .then((dataFieldValues) => dataFieldValues)
        .catch((err) => failedToLoad(err));

      let editDrAllCustomFields: Record<string, CustomField[]> = {};
      Promise.all(
        Object.keys(fieldValues).map(async (decisionRegistryId) => {
          const drFieldValues = fieldValues[decisionRegistryId].reduce(
            (acc: Record<string, string | string[]>, tuple: string) => {
              const fieldPair = tuple.split(',');
              const key = fieldPair[0].substr(1);
              const value = fieldPair[1].slice(0, -1);
              const fieldType =
                fieldAttr[key as keyof FieldAttributesHash]
                  .fCustomDataAttributeDataType;
              if (acc[key]) {
                const keyValue = acc[key] as string[];
                acc[key] = [...keyValue, value];
              } else {
                acc[key] =
                  fieldType === CustomFieldType.MULTISELECT ? [value] : value;
              }
              return acc;
            },
            {},
          );
          const editCustomField = await editCustomFieldDataOld(
            empStatus,
            empPositionData as EmpPosition,
            fieldAttr,
            fieldLang,
            drFieldValues ? drFieldValues : {},
            empPositionPendingLegalEntity,
            allEnums,
            legalEntities,
          );
          editDrAllCustomFields[decisionRegistryId] = editCustomField;
          return true;
        }),
      ).then(async () => {
        let addDrEmptyCustomFields: CustomField[] = [];
        if (Object.keys(fieldAttr).length && fieldLang.length) {
          addDrEmptyCustomFields = await editCustomFieldDataOld(
            empStatus,
            empPositionData as EmpPosition,
            fieldAttr,
            fieldLang,
            {},
            empPositionPendingLegalEntity,
            allEnums,
            legalEntities,
          );
          const filteredSelectFieldsCodes = Object.keys(fieldAttr).filter(
            (code: string) => {
              if (
                fieldAttr[code]?.fCustomDataAttributeDataType ===
                  CustomFieldType.ENUM ||
                fieldAttr[code].fCustomDataAttributeDataType ===
                  CustomFieldType.MULTISELECT
              ) {
                return true;
              } else {
                return false;
              }
            },
          );
          setCustomSelectFields(filteredSelectFieldsCodes as []);
          // Manually add enums, multiselects to form
          customSelectFields.forEach((code: string) => {
            register(code);
          });
        }
        setData({
          decisionRegistryData: response,
          editDrAllCustomFields,
          fieldLang,
          addDrEmptyCustomFields,
          isFetching: false,
        });
      });
    } catch (e) {
      setData({
        decisionRegistryData: data.decisionRegistryData,
        editDrAllCustomFields: {},
        fieldLang: [],
        addDrEmptyCustomFields: [],
        isFetching: false,
      });
    }
  }, [
    DECISION_REGISTRY_GET,
    allEnums,
    customSelectFields,
    data.decisionRegistryData,
    empPositionData,
    empPositionPendingLegalEntity,
    empStatus,
    empId,
    failedToLoad,
    legalEntities,
    register,
  ]);

  useEffect(() => {
    if (!data.isFetching) return;
    fetchDecisionRegistryData();
  }, [data.isFetching, fetchDecisionRegistryData]);

  return { data, customSelectFields, fetchDecisionRegistryData };
};
